import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography as MuiTypography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import React, { useState } from 'react';

import InfoCard from 'components/InfoCard/InfoCard';
import Typography from 'components/Typography/Typography';
import { useResponsive } from 'contexts/ResponsiveContext';
import type { ClaimReportCardI } from 'declarations/interfaces/reports';
import CardLoader from 'pages/TicketsPage/components/CardLoader/CardLoader';
import { CardLoaderWrapStyled } from 'pages/TicketsPage/components/CardLoader/CardLoaderWrapStyled';
import { ScheduleCardCompactWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import {
  ScheduleCompactInnerStyled,
  ScheduleTitleStyled,
} from '../ScheduleCards/ScheduleCards.styled';

import {
  CardsWrapStyled,
  ChangePercentTextStyled,
  ChangeTriangleStyled,
} from './DealerReport.styled';
import useDealerReport from './hooks/useDealerReport';

type DealerReportT = { isReportOpened: boolean };

const formatAmount = (value: number): string =>
  Math.abs(value).toLocaleString('en-US', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

const percentChange = (left: number, right: number | null): number | null => {
  if (right === null || left === 0) return null;
  return ((right - left) / left) * 100;
};

type ChangeVariantT = 'zero' | 'increase' | 'decrease';
const getChangeVariant = (change: number): ChangeVariantT => {
  if (change === 0) return 'zero';
  return change > 0 ? 'increase' : 'decrease';
};

const renderChangeCell = (change: number | null): React.ReactNode => {
  if (change === null) return null;
  const variant = getChangeVariant(change);
  const isIncrease = change > 0;
  return (
    <>
      {change !== 0 && <ChangeTriangleStyled $isIncrease={isIncrease} />}
      <ChangePercentTextStyled $variant={variant}>{change.toFixed(2)}%</ChangePercentTextStyled>
    </>
  );
};

const ROW_METRICS: Array<{
  key: string;
  label: string;
  getValue: (p: ClaimReportCardI) => string | number;
  prefix?: string;
  getNumericValue: (p: ClaimReportCardI) => number;
}> = [
  {
    key: 'amount',
    label: 'Amount',
    getValue: p => formatAmount(p.totalClaimAmount),
    prefix: '$',
    getNumericValue: p => p.totalClaimAmount,
  },
  {
    key: 'claims',
    label: 'Claims',
    getValue: p => p.totalClaimCount,
    getNumericValue: p => p.totalClaimCount,
  },
  {
    key: 'avgClaim',
    label: 'Avg. Claim Amount',
    getValue: p => p.averageClaimAmount.toFixed(2),
    prefix: '$',
    getNumericValue: p => p.averageClaimAmount,
  },
];

const DealerReport: React.FC<DealerReportT> = ({ isReportOpened }) => {
  const theme = useTheme();
  const { isMobile } = useResponsive();
  const [isBillingCardExpanded, setIsBillingCardExpanded] = useState(false);
  const { claimReport, isLoadingReport } = useDealerReport();
  const warrCloudPurple = theme.palette.secondary.main;

  if (isLoadingReport) {
    return (
      <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
        <ScheduleCardCompactWrapStyled>
          {isMobile ? (
            <Accordion
              expanded={isBillingCardExpanded}
              onChange={(_e, exp): void => setIsBillingCardExpanded(exp)}
              disableGutters
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&.MuiAccordion-root': {
                  backgroundColor: (t: Theme): string => t.palette.background.paper,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                sx={{
                  minHeight: 46,
                  '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    transform: 'none',
                    '&.Mui-expanded': { transform: 'rotate(180deg)' },
                  },
                }}
              >
                <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>
                  Billing Periods
                </MuiTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                <InfoCard fullWidth={false} compact>
                  <ScheduleCompactInnerStyled $compact={true}>
                    <CardLoaderWrapStyled>
                      <CardLoader />
                    </CardLoaderWrapStyled>
                  </ScheduleCompactInnerStyled>
                </InfoCard>
              </AccordionDetails>
            </Accordion>
          ) : (
            <InfoCard fullWidth={false} compact>
              <ScheduleCompactInnerStyled $compact={true}>
                <ScheduleTitleStyled variant="body2" weight="normal" $compact={true}>
                  Billing Periods
                </ScheduleTitleStyled>
                <CardLoaderWrapStyled>
                  <CardLoader />
                </CardLoaderWrapStyled>
              </ScheduleCompactInnerStyled>
            </InfoCard>
          )}
        </ScheduleCardCompactWrapStyled>
      </CardsWrapStyled>
    );
  }

  if (!claimReport || claimReport.length === 0) {
    return (
      <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
        <ScheduleCardCompactWrapStyled>
          {isMobile ? (
            <Accordion
              expanded={isBillingCardExpanded}
              onChange={(_e, exp): void => setIsBillingCardExpanded(exp)}
              disableGutters
              sx={{
                boxShadow: 'none',
                '&:before': { display: 'none' },
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&.MuiAccordion-root': {
                  backgroundColor: (t: Theme): string => t.palette.background.paper,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                sx={{
                  minHeight: 46,
                  '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    transform: 'none',
                    '&.Mui-expanded': { transform: 'rotate(180deg)' },
                  },
                }}
              >
                <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>
                  Billing Periods
                </MuiTypography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
                <InfoCard fullWidth={false} compact>
                  <ScheduleCompactInnerStyled $compact={true}>
                    <Typography variant="body4" color="darkGrey">
                      No claim report data
                    </Typography>
                  </ScheduleCompactInnerStyled>
                </InfoCard>
              </AccordionDetails>
            </Accordion>
          ) : (
            <InfoCard fullWidth={false} compact>
              <ScheduleCompactInnerStyled $compact={true}>
                <ScheduleTitleStyled variant="body2" weight="normal" $compact={true}>
                  Billing Periods
                </ScheduleTitleStyled>
                <Typography variant="body4" color="darkGrey">
                  No claim report data
                </Typography>
              </ScheduleCompactInnerStyled>
            </InfoCard>
          )}
        </ScheduleCardCompactWrapStyled>
      </CardsWrapStyled>
    );
  }

  const periodsOrdered = claimReport;

  const getBillingHeaderTooltip = (idx: number): React.ReactNode => {
    switch (idx) {
      case 0:
        return (
          <>
            This is your first month&apos;s billing with{' '}
            <Box component="span" sx={{ color: warrCloudPurple, fontWeight: 600 }}>
              WarrCloud
            </Box>
            .
          </>
        );
      case 1:
        return "This is your billing from 2 months ago; directly compared to your first month's billing.";
      case 2:
        return "This is your previous month's billing; compared to your billing from 2 months ago.";
      default:
        return '';
    }
  };

  const handleBillingExpandChange = (_e: React.SyntheticEvent, expanded: boolean): void => {
    setIsBillingCardExpanded(expanded);
  };

  const tableSx = {
    '& .MuiTableCell-root': { py: 1.5, px: 0.75 },
    '& .MuiTableHead .MuiTableCell-root': {
      fontSize: '0.813rem',
      paddingTop: 1,
      paddingBottom: 1,
      lineHeight: 1,
    },
    '& .MuiTableBody .MuiTableCell-root': { fontSize: '0.813rem' },
    '& .MuiTableRow-root td, & .MuiTableRow-root th': { borderBottom: 'none' },
    '& .MuiTableCell-root:first-of-type': { maxWidth: 100, paddingRight: 0.5 },
  };

  const tableContent = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        minWidth: 320,
        py: 0.5,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      <Table size="small" aria-label="Billing periods by metric" sx={tableSx}>
        <TableHead>
          <TableRow sx={{ '& > th': { paddingTop: 1, paddingBottom: 1, lineHeight: 1 } }}>
            <TableCell
              component="th"
              scope="col"
              padding="none"
              sx={{ pr: 0.5, paddingTop: 1, paddingBottom: 1 }}
            />
            {periodsOrdered.map((period, idx) => (
              <React.Fragment key={`header-${idx}`}>
                <TableCell
                  align="center"
                  padding="none"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    lineHeight: 1,
                    paddingTop: 1,
                    paddingBottom: 1,
                    backgroundColor: (t: Theme) => t.palette.background?.default ?? '#F8F9FA',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {period.invoiceDateStr}
                    <Tooltip title={getBillingHeaderTooltip(idx)} arrow placement="top">
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 14,
                          ml: 0.25,
                          opacity: 0.7,
                        }}
                      />
                    </Tooltip>
                  </Box>
                </TableCell>
                {idx < periodsOrdered.length - 1 && (
                  <TableCell
                    component="th"
                    scope="col"
                    align="center"
                    padding="none"
                    sx={{
                      minWidth: 52,
                      fontWeight: 400,
                      paddingTop: 1,
                      paddingBottom: 1,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROW_METRICS.map(metric => (
            <TableRow key={metric.key}>
              <TableCell sx={{ fontWeight: 600, pr: 0.5 }}>{metric.label}</TableCell>
              {periodsOrdered.map((period, idx) => {
                const value = metric.getValue(period);
                const prefix = metric.prefix ?? '';
                const nextPeriod = idx < periodsOrdered.length - 1 ? periodsOrdered[idx + 1] : null;
                const change = nextPeriod
                  ? percentChange(
                      metric.getNumericValue(period),
                      metric.getNumericValue(nextPeriod)
                    )
                  : null;

                return (
                  <React.Fragment key={`${metric.key}-${idx}`}>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>
                      {prefix}
                      {value}
                    </TableCell>
                    {idx < periodsOrdered.length - 1 && (
                      <TableCell align="center" sx={{ minWidth: 52 }}>
                        {renderChangeCell(change)}
                      </TableCell>
                    )}
                  </React.Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  if (isMobile) {
    return (
      <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
        <ScheduleCardCompactWrapStyled>
          <Accordion
            expanded={isBillingCardExpanded}
            onChange={handleBillingExpandChange}
            disableGutters
            sx={{
              boxShadow: 'none',
              '&:before': { display: 'none' },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              '&.MuiAccordion-root': {
                backgroundColor: (t: Theme): string => t.palette.background.paper,
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
              sx={{
                minHeight: 46,
                '& .MuiAccordionSummary-content': { margin: 0, alignItems: 'center' },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  transform: 'none',
                  '&.Mui-expanded': { transform: 'rotate(180deg)' },
                },
              }}
            >
              <MuiTypography sx={{ fontSize: 15, fontWeight: 500 }}>Billing Periods</MuiTypography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 0 }}>
              <InfoCard fullWidth={false} compact>
                <ScheduleCompactInnerStyled $compact={true}>
                  {tableContent}
                </ScheduleCompactInnerStyled>
              </InfoCard>
            </AccordionDetails>
          </Accordion>
        </ScheduleCardCompactWrapStyled>
      </CardsWrapStyled>
    );
  }

  return (
    <CardsWrapStyled justify="start" align="stretch" isReportOpened={isReportOpened}>
      <ScheduleCardCompactWrapStyled>
        <InfoCard fullWidth={false} compact>
          <ScheduleCompactInnerStyled $compact={true}>
            <Box sx={{ mb: 0.5, '& > *': { marginBottom: 0 } }}>
              <ScheduleTitleStyled variant="body2" weight="normal" $compact={true}>
                Billing Periods
              </ScheduleTitleStyled>
            </Box>
            {tableContent}
          </ScheduleCompactInnerStyled>
        </InfoCard>
      </ScheduleCardCompactWrapStyled>
    </CardsWrapStyled>
  );
};

export default DealerReport;
